import { useCallback, useRef, useState } from 'react';
import {
  AbortStreamFunction,
  ChatMessageChunk,
  ChatMessageChunkType,
  EntityContext,
  FeedbackCategory,
  LlmMessage,
  useLlmChatService,
  useLlmConversationsService,
} from 'nekst-api';
import { useEventsPublisher } from 'event-bus';
import { useDataLoaderNew } from 'data-loader';
import useFeatureToggleService from 'feature-toggle';
import { ChatMessage, ChatSessionContextValue, MessageRating, UpdateDescriptionPayload } from './types';
import { consumeTransferState } from './chatTransferStore';
import { detachRelayListener } from './streamRelay';
import { useStreamRelay } from './useStreamRelay';

function createUserMessage(content: string, context?: EntityContext[]): ChatMessage {
  return {
    role: 'user',
    content,
    timestamp: new Date(),
    context: context && context.length > 0 ? context : undefined,
  };
}

function createAssistantMessage(content: string): ChatMessage {
  return {
    role: 'assistant',
    content,
    timestamp: new Date(),
  };
}

function createToolActivityMessage(displayMessage: string): ChatMessage {
  return {
    role: 'tool-activity',
    content: displayMessage,
    timestamp: new Date(),
  };
}

function createActionCardMessage(action: PendingActionData): ChatMessage {
  return {
    role: 'action-card',
    content: '',
    timestamp: new Date(),
    pendingAction: action,
  };
}

function createErrorMessage(error: string): ChatMessage {
  return {
    role: 'assistant',
    content: error,
    timestamp: new Date(),
    isError: true,
  };
}

function convertApiMessages(apiMessages: LlmMessage[]): ChatMessage[] {
  return apiMessages
    .filter(
      (msg): msg is LlmMessage & { content: string; role: 'user' | 'assistant' } =>
        msg.content !== null && (msg.role === 'user' || msg.role === 'assistant'),
    )
    .map((msg) => ({
      id: msg.id,
      role: msg.role,
      content: msg.content,
      timestamp: msg.createdAt,
      context: msg.context ?? undefined,
      rating: msg.rating ?? undefined,
    }));
}

export const CONVERSATION_CREATED_EVENT = 'LLM_CONVERSATION_CREATED';
export const CONVERSATION_TITLE_GENERATED_EVENT = 'LLM_CONVERSATION_TITLE_GENERATED';
export const CONVERSATION_DELETED_EVENT = 'LLM_CONVERSATION_DELETED';
export const CONVERSATION_TITLE_UPDATED_EVENT = 'LLM_CONVERSATION_TITLE_UPDATED';

export function useChatSession(conversationId?: number): ChatSessionContextValue {
  const initialTransfer = useRef(
    conversationId ? consumeTransferState(conversationId) : null,
  );

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const transfer = initialTransfer.current;
    if (transfer) {
      let msgs = transfer.messages;
      if (transfer.streamingText) {
        msgs = [...msgs, createAssistantMessage(transfer.streamingText)];
      }
      return msgs;
    }
    return [];
  });
  const [streamingText, setStreamingText] = useState(
    () => initialTransfer.current?.streamingText ?? '',
  );
  const [isStreaming, setIsStreaming] = useState(
    () => initialTransfer.current?.isStreaming ?? false,
  );
  const [isToolExecuting, setIsToolExecuting] = useState(false);
  const [inputText, setInputText] = useState('');
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(
    () => initialTransfer.current?.conversationId ?? null,
  );
  const [selectedContext, setSelectedContext] = useState<EntityContext[]>(
    () => initialTransfer.current?.selectedContext ?? [],
  );
  const [editingMessageId, setEditingMessageId] = useState<number | null>(null);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [activeModelId, setActiveModelId] = useState<string | null>(null);
  const [totalConversationCost, setTotalConversationCost] = useState(0);

  const abortRef = useRef<AbortStreamFunction | null>(null);
  const conversationIdRef = useRef<number | null>(
    initialTransfer.current?.conversationId ?? null,
  );
  const streamingTextRef = useRef(initialTransfer.current?.streamingText ?? '');

  const chatService = useLlmChatService();
  const conversationsService = useLlmConversationsService();
  const eventsPublisher = useEventsPublisher();
  const { isEmailDescriptionToolEnabled } = useFeatureToggleService();

  const { loading: isLoadingConversation } = useDataLoaderNew(
    async () => {
      if (conversationId) {
        if (initialTransfer.current) {
          initialTransfer.current = null;
          return;
        }

        if (abortRef.current) {
          abortRef.current();
          abortRef.current = null;
        }

        const [apiMessages, conversation] = await Promise.all([
          conversationsService.getConversationMessages(conversationId),
          conversationsService.getConversation(conversationId),
        ]);
        setMessages(convertApiMessages(apiMessages));
        streamingTextRef.current = '';
        setStreamingText('');
        setIsStreaming(false);
        setSelectedContext([]);
        setEditingMessageId(null);
        setActiveModelId(conversation.model);
        setTotalConversationCost(0);

        conversationIdRef.current = conversationId;
        setCurrentConversationId(conversationId);
        setIsToolExecuting(false);
        setInputText('');
      } else {
        if (abortRef.current) {
          abortRef.current();
          abortRef.current = null;
        }
        setMessages([]);
        streamingTextRef.current = '';
        setStreamingText('');
        setIsStreaming(false);
        setIsToolExecuting(false);
        setInputText('');
        setCurrentConversationId(null);
        conversationIdRef.current = null;
        setSelectedContext([]);
        setEditingMessageId(null);
        setActiveModelId(null);
        setTotalConversationCost(0);
      }
    },
    { dependencies: [conversationId] },
  );

  const finalizeStreamedMessage = useCallback((cost?: number) => {
    const completedText = streamingTextRef.current;
    if (completedText) {
      const message = createAssistantMessage(completedText);
      if (cost != null) {
        message.estimatedCostUsd = cost;
      }
      setMessages((prev) => [...prev, message]);
    }
    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
  }, []);

  const handleChunk = useCallback((chunk: ChatMessageChunk) => {
    if (chunk.type === ChatMessageChunkType.TEXT_DELTA && chunk.text) {
      setIsToolExecuting(false);
      streamingTextRef.current += chunk.text;
      setStreamingText(streamingTextRef.current);
    } else if (chunk.type === ChatMessageChunkType.USER_MESSAGE_SAVED && chunk.messageId) {
      setMessages((prev) => {
        const lastUserIndex = prev.reduce<number>(
          (acc, msg, i) => (msg.role === 'user' ? i : acc),
          -1,
        );
        if (lastUserIndex === -1) {
          return prev;
        }
        const updated = [...prev];
        updated[lastUserIndex] = { ...updated[lastUserIndex], id: chunk.messageId };
        return updated;
      });
    } else if (chunk.type === ChatMessageChunkType.CONVERSATION_CREATED && chunk.conversation) {
      conversationIdRef.current = chunk.conversation.id;
      setCurrentConversationId(chunk.conversation.id);
      if (chunk.conversation.model) {
        setActiveModelId(chunk.conversation.model);
      }
      eventsPublisher.publish(CONVERSATION_CREATED_EVENT, { conversation: chunk.conversation });
    } else if (chunk.type === ChatMessageChunkType.TOOL_CALL && chunk.displayMessage) {
      setIsToolExecuting(true);
      const pendingText = streamingTextRef.current;
      if (pendingText) {
        streamingTextRef.current = '';
        setStreamingText('');
        setMessages((prev) => [
          ...prev,
          createAssistantMessage(pendingText),
          createToolActivityMessage(chunk.displayMessage!),
        ]);
      } else {
        setMessages((prev) => [...prev, createToolActivityMessage(chunk.displayMessage!)]);
      }
    } else if (chunk.type === ChatMessageChunkType.FINISH) {
      setIsToolExecuting(false);
      if (chunk.estimatedCostUsd != null) {
        setTotalConversationCost((prev) => prev + chunk.estimatedCostUsd!);
      }
      finalizeStreamedMessage(chunk.estimatedCostUsd ?? undefined);
    } else if (chunk.type === ChatMessageChunkType.ASSISTANT_MESSAGE_SAVED && chunk.messageId) {
      setMessages((prev) => {
        const index = prev.findIndex((m) => m.role === 'assistant' && !m.id);
        if (index === -1) {
          return prev;
        }
        const updated = [...prev];
        updated[index] = { ...updated[index], id: chunk.messageId };
        return updated;
      });
    } else if (chunk.type === ChatMessageChunkType.TITLE_GENERATED && chunk.text) {
      if (conversationIdRef.current) {
        eventsPublisher.publish(CONVERSATION_TITLE_GENERATED_EVENT, {
          conversationId: conversationIdRef.current,
          title: chunk.text,
        });
      }
    } else if (chunk.type === ChatMessageChunkType.PENDING_ACTION && chunk.actionType && chunk.actionPayload) {
      setMessages((prev) => [...prev, createActionCardMessage({
        actionType: chunk.actionType!,
        payload: chunk.actionPayload! as UpdateDescriptionPayload,
      })]);
    } else if (chunk.type === ChatMessageChunkType.ERROR) {
      setIsToolExecuting(false);
      detachRelayListener();
      setMessages((prev) => [...prev, createErrorMessage(chunk.error || 'An error occurred')]);
      streamingTextRef.current = '';
      setStreamingText('');
      setIsStreaming(false);
    }
  }, [finalizeStreamedMessage]);

  const { startRelay, stopRelay } = useStreamRelay(
    handleChunk,
    initialTransfer.current?.isStreaming ?? false,
  );

  const stopGeneration = useCallback(() => {
    if (!isStreaming) {
      return;
    }

    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }

    detachRelayListener();
    stopRelay();
    setIsToolExecuting(false);

    setMessages((prev) => {
      const filtered = prev.filter((m) => m.role !== 'tool-activity');
      const pendingText = streamingTextRef.current;
      if (pendingText) {
        return [...filtered, createAssistantMessage(pendingText)];
      }
      return filtered;
    });

    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
  }, [isStreaming, stopRelay]);

  const sendMessage = useCallback(() => {
    const trimmed = inputText.trim();
    if (!trimmed || isStreaming) {
      return;
    }

    const contextToSend = selectedContext.length > 0 ? selectedContext : undefined;
    setMessages((prev) => [...prev, createUserMessage(trimmed, contextToSend)]);
    setInputText('');
    setSelectedContext([]);
    setIsStreaming(true);
    streamingTextRef.current = '';
    setStreamingText('');

    const dispatcher = startRelay();

    const enableEmailDescriptionTool = isEmailDescriptionToolEnabled() || undefined;

    if (conversationIdRef.current) {
      abortRef.current = chatService.sendMessage(
        conversationIdRef.current,
        { message: trimmed, context: contextToSend, enableEmailDescriptionTool },
        dispatcher,
      );
    } else {
      abortRef.current = chatService.startConversation(
        { message: trimmed, model: selectedModelId ?? undefined, context: contextToSend, enableEmailDescriptionTool },
        dispatcher,
      );
    }
  }, [inputText, isStreaming, selectedContext, selectedModelId, chatService, startRelay]);

  const editMessage = useCallback((messageId: number, newContent: string) => {
    if (isStreaming || !conversationIdRef.current) {
      return;
    }

    const messageIndex = messages.findIndex((m) => m.id === messageId);
    if (messageIndex === -1) {
      return;
    }
    const newUserMessage = createUserMessage(newContent);

    setEditingMessageId(null);
    setMessages((prev) => [...prev.slice(0, messageIndex), newUserMessage]);
    setIsStreaming(true);
    streamingTextRef.current = '';
    setStreamingText('');

    const enableEmailDescriptionTool = isEmailDescriptionToolEnabled() || undefined;

    const dispatcher = startRelay();
    abortRef.current = chatService.editMessage(
      conversationIdRef.current!,
      messageId,
      { message: newContent, enableEmailDescriptionTool },
      dispatcher,
    );
  }, [isStreaming, messages, chatService, startRelay]);

  const resetConversation = useCallback(() => {
    if (abortRef.current) {
      abortRef.current();
      abortRef.current = null;
    }
    stopRelay();
    setMessages([]);
    streamingTextRef.current = '';
    setStreamingText('');
    setIsStreaming(false);
    setIsToolExecuting(false);
    setInputText('');
    setCurrentConversationId(null);
    conversationIdRef.current = null;
    setSelectedContext([]);
    setActiveModelId(null);
    setTotalConversationCost(0);
    setSelectedModelId(null);
  }, [stopRelay]);

  const addContext = useCallback((context: EntityContext) => {
    setSelectedContext((prev) => {
      const exists = prev.some((c) => c.type === context.type && c.id === context.id);
      if (exists) {
        return prev;
      }
      return [...prev, context];
    });
  }, []);

  const removeContext = useCallback((context: EntityContext) => {
    setSelectedContext((prev) =>
      prev.filter((c) => !(c.type === context.type && c.id === context.id)),
    );
  }, []);

  const clearContext = useCallback(() => {
    setSelectedContext([]);
  }, []);

  const submitFeedback = useCallback(async (
    description: string,
    category?: FeedbackCategory | null,
  ): Promise<void> => {
    if (!conversationIdRef.current) {
      throw new Error('No active conversation');
    }
    await conversationsService.submitFeedback(conversationIdRef.current, {
      description,
      category: category ?? null,
    });
  }, [conversationsService]);

  const rateMessage = useCallback((messageId: number, rating: MessageRating | null) => {
    let previousRating: MessageRating | undefined;

    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id === messageId) {
          previousRating = msg.rating;
          return { ...msg, rating: rating ?? undefined };
        }
        return msg;
      }),
    );

    conversationsService.rateMessage(messageId, rating).catch(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, rating: previousRating } : msg,
        ),
      );
    });
  }, [conversationsService]);

  return {
    messages,
    streamingText,
    isStreaming,
    isToolExecuting,
    isLoadingConversation: isLoadingConversation && !!conversationId && messages.length === 0,
    inputText,
    currentConversationId,
    selectedContext,
    editingMessageId,
    selectedModelId,
    activeModelId,
    totalConversationCost,
    sendMessage,
    stopGeneration,
    editMessage,
    setEditingMessageId,
    rateMessage,
    submitFeedback,
    resetConversation,
    setInputText,
    addContext,
    removeContext,
    clearContext,
    setSelectedModelId,
  };
}
